import { useMemo, useState } from 'react'
import { api, ROUTES, imgUrl } from './api'
import { useAdmin, useAsync, Modal, Pager, ErrorState, Loading, Empty, PageHead, Status, money, dateFmt } from './ui'

const STATUSES = ['Processing', 'Shipped', 'Delivered', 'Cancelled']
const PER = 10

export default function Orders() {
  const { data, loading, error, reload } = useAsync(() => api.get(ROUTES.adminOrdersAll))
  const [tab, setTab] = useState('All')
  const [q, setQ] = useState('')
  const [page, setPage] = useState(1)
  const [openId, setOpenId] = useState(null)
  const orders = data || []

  const filtered = useMemo(
    () =>
      orders.filter((o) => {
        if (tab !== 'All' && o.status !== tab) return false
        const hay = `${o.orderNo} ${o.customer ?? o.ship_name ?? ''}`.toLowerCase()
        return !q || hay.includes(q.toLowerCase())
      }),
    [orders, tab, q]
  )
  const rows = filtered.slice((page - 1) * PER, page * PER)

  if (error) return <ErrorState error={error} endpoint={`GET ${ROUTES.adminOrders}`} onRetry={reload} />

  return (
    <>
      <PageHead title="Orders" sub="Review orders and move them through fulfilment." />
      <div className="tabs" role="tablist">
        {['All', ...STATUSES].map((s) => (
          <button key={s} role="tab" aria-selected={tab === s} className={`tab${tab === s ? ' on' : ''}`} onClick={() => { setTab(s); setPage(1) }}>
            {s} <span className="muted">{s === 'All' ? orders.length : orders.filter((o) => o.status === s).length}</span>
          </button>
        ))}
      </div>
      <div className="toolbar"><input className="input" placeholder="Search by order number or customer" value={q} onChange={(e) => { setQ(e.target.value); setPage(1) }} /></div>

      {loading ? <Loading /> : filtered.length === 0 ? <Empty title="No orders found" /> : (
        <div className="table-wrap">
          <table className="table">
            <thead><tr><th>Order</th><th>Customer</th><th>Date</th><th>Items</th><th>Payment</th><th>Total</th><th>Status</th></tr></thead>
            <tbody>
              {rows.map((o) => (
                <tr key={o.id} className="click" onClick={() => setOpenId(o.id)}>
                  <td><button className="link" onClick={() => setOpenId(o.id)}>{o.orderNo}</button></td>
                  <td>{o.customer ?? o.ship_name ?? '-'}</td>
                  <td>{dateFmt(o.createdAt)}</td>
                  <td>{o.items ?? '-'}</td>
                  <td>{o.paymentMethod ?? '-'}</td>
                  <td>{money(o.total)}</td>
                  <td><Status value={o.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Pager page={page} total={filtered.length} per={PER} onPage={setPage} />
      {openId && <OrderDetail id={openId} onClose={() => setOpenId(null)} onChanged={reload} />}
    </>
  )
}

function OrderDetail({ id, onClose, onChanged }) {
  const { toast } = useAdmin()
  const { data: o, loading, error } = useAsync(() => api.get(`${ROUTES.adminOrders}/${id}`), [id])
  const [status, setStatus] = useState(null)
  const [saving, setSaving] = useState(false)
  const current = status ?? o?.status

  const save = async () => {
    setSaving(true)
    try {
      await api.put(`${ROUTES.adminOrders}/${id}/status`, { status: current })
      toast(`Order marked ${current}`)
      onChanged()
      onClose()
    } catch (e) {
      toast(e.message, 'err')
      setSaving(false)
    }
  }

  return (
    <Modal
      wide
      title={o ? `Order ${o.orderNo}` : 'Order'}
      onClose={onClose}
      footer={o && (
        <>
          <select className="input auto" value={current} onChange={(e) => setStatus(e.target.value)} aria-label="Order status">
            {STATUSES.map((s) => <option key={s}>{s}</option>)}
          </select>
          <button className="btn primary" disabled={saving || current === o.status} onClick={save}>{saving ? 'Saving…' : 'Update status'}</button>
        </>
      )}
    >
      {loading ? <Loading /> : error ? <ErrorState error={error} /> : (
        <div className="order">
          <p className="muted">{dateFmt(o.createdAt)} · {o.paymentMethod} ({o.paymentStatus || 'n/a'}) · {o.deliveryMethod} delivery</p>
          <table className="table compact">
            <tbody>
              {o.items.map((i) => (
                <tr key={i.id}>
                  <td><div className="cell-media">{i.image ? <img className="thumb" src={imgUrl(i.image)} alt="" /> : <span className="thumb" />}<span>{i.name}</span></div></td>
                  <td>× {i.quantity}</td>
                  <td className="num">{money(i.price * i.quantity)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <dl className="sum">
            <div><dt>Subtotal</dt><dd>{money(o.subtotal)}</dd></div>
            {o.discount > 0 && <div><dt>Discount ({o.coupons.join(', ')})</dt><dd>- {money(o.discount)}</dd></div>}
            <div><dt>Shipping</dt><dd>{money(o.shipping)}</dd></div>
            <div className="total"><dt>Total</dt><dd>{money(o.total)}</dd></div>
          </dl>
          <div className="two-col tight">
            <div>
              <h3>Ship to</h3>
              <p>{o.address.name}<br />{o.address.line}<br />{o.address.city}, {o.address.state} {o.address.pincode}<br />{o.address.phone}</p>
            </div>
            <div>
              <h3>Contact and notes</h3>
              <p>{o.email || 'No email'}<br />{o.notes || 'No notes'}</p>
            </div>
          </div>
        </div>
      )}
    </Modal>
  )
}
